import type { Firestore, Unsubscribe } from 'firebase/firestore';

import { getFirebaseApp } from '@/services/firebase/config';

let firestoreInstance: Firestore | null = null;

const FIRESTORE_DATABASE_ID = 'cv-database';

export async function getFirestoreDb(): Promise<Firestore> {
  if (firestoreInstance) return firestoreInstance;
  const app = await getFirebaseApp();
  const { getFirestore } = await import('firebase/firestore');
  firestoreInstance = getFirestore(app, FIRESTORE_DATABASE_ID);
  return firestoreInstance;
}

export interface RecoveryEnvelopeDoc {
  ciphertext: string;
  iv: string;
  salt: string;
}

export interface UserProfileDoc {
  email: string | null;
  createdAt: string;
  encryptionSalt: string;
  schemaVersion: number;
  recoveryEnvelope?: RecoveryEnvelopeDoc;
}

export interface EncryptedCvDoc {
  encryptedData: string;
  iv: string;
  updatedAt: string;
  schemaVersion: number;
  photoIv?: string;
  photoMimeType?: string;
}

export async function getUserProfile(userId: string): Promise<UserProfileDoc | null> {
  const db = await getFirestoreDb();
  const { doc, getDoc } = await import('firebase/firestore');
  const ref = doc(db, 'users', userId);
  const snap = await getDoc(ref);
  if (!snap.exists()) return null;
  return snap.data() as UserProfileDoc;
}

export async function setUserProfile(userId: string, profile: UserProfileDoc): Promise<void> {
  const db = await getFirestoreDb();
  const { doc, setDoc } = await import('firebase/firestore');
  const ref = doc(db, 'users', userId);
  await setDoc(ref, profile, { merge: true });
}

export async function listEncryptedCvs(
  userId: string,
): Promise<Array<EncryptedCvDoc & { id: string }>> {
  const db = await getFirestoreDb();
  const { collection, getDocs } = await import('firebase/firestore');
  const ref = collection(db, 'users', userId, 'cvs');
  const snap = await getDocs(ref);
  return snap.docs.map((d) => ({ id: d.id, ...(d.data() as EncryptedCvDoc) }));
}

export async function getEncryptedCv(userId: string, cvId: string): Promise<EncryptedCvDoc | null> {
  const db = await getFirestoreDb();
  const { doc, getDoc } = await import('firebase/firestore');
  const ref = doc(db, 'users', userId, 'cvs', cvId);
  const snap = await getDoc(ref);
  if (!snap.exists()) return null;
  return snap.data() as EncryptedCvDoc;
}

export async function setEncryptedCv(
  userId: string,
  cvId: string,
  payload: EncryptedCvDoc,
): Promise<void> {
  const db = await getFirestoreDb();
  const { doc, setDoc } = await import('firebase/firestore');
  const ref = doc(db, 'users', userId, 'cvs', cvId);
  await setDoc(ref, payload);
}

export async function deleteEncryptedCv(userId: string, cvId: string): Promise<void> {
  const db = await getFirestoreDb();
  const { doc, deleteDoc } = await import('firebase/firestore');
  const ref = doc(db, 'users', userId, 'cvs', cvId);
  await deleteDoc(ref);
}

export async function deleteAllUserData(userId: string): Promise<void> {
  const db = await getFirestoreDb();
  const { collection, getDocs, doc, deleteDoc, writeBatch } = await import('firebase/firestore');
  const cvsRef = collection(db, 'users', userId, 'cvs');
  const snap = await getDocs(cvsRef);
  if (snap.docs.length > 0) {
    const batch = writeBatch(db);
    snap.docs.forEach((d) => batch.delete(d.ref));
    await batch.commit();
  }
  await deleteDoc(doc(db, 'users', userId));
}

export async function watchEncryptedCvs(
  userId: string,
  callback: (docs: Array<EncryptedCvDoc & { id: string }>) => void,
): Promise<Unsubscribe> {
  const db = await getFirestoreDb();
  const { collection, onSnapshot } = await import('firebase/firestore');
  const ref = collection(db, 'users', userId, 'cvs');
  return onSnapshot(ref, (snap) => {
    callback(snap.docs.map((d) => ({ id: d.id, ...(d.data() as EncryptedCvDoc) })));
  });
}
